import axiosInstance from '@/api/axiosInstance';
import {
  StyleSearchParams,
  StyleListResponse,
  StyleListApiResponse,
  StyleDetailApiResponse,
  StyleRecord,
  StyleFormData,
  ModelOption,
} from './types';
import { artworkLiblibTypeStyle } from '@/api/liblib';

const baseUrl = `${SUPP_ADMIN_BASE_URL}/supp-management`;

/**
 * 获取风格列表
 */
export const getStyleList = async (
  params: StyleSearchParams
): Promise<StyleListResponse> => {
  const pageNum = params.pageNum ?? 1;
  const pageSize = params.pageSize ?? 10;

  // 构建请求体（只包含搜索条件）
  const requestBody: Record<string, any> = {};
  if (params.styleName) {
    requestBody.styleName = params.styleName;
  }

  const response = await axiosInstance.post<StyleListApiResponse>(
    `${baseUrl}/artworkAiDrawStyle/page?pageNum=${pageNum}&pageSize=${pageSize}`,
    requestBody
  );

  if (!response.data.success || !response.data.data) {
    throw new Error(response.data.message || '获取风格列表失败');
  }

  return response.data.data;
};

/**
 * 获取风格详情
 */
export const getStyleDetail = async (styleId: number): Promise<StyleRecord> => {
  const response = await axiosInstance.get<StyleDetailApiResponse>(
    `${baseUrl}/artworkAiDrawStyle/detail/${styleId}`
  );

  if (!response.data.success || !response.data.data) {
    throw new Error(response.data.message || '获取风格详情失败');
  }

  return response.data.data;
};

/**
 * API 响应基础结构
 */
interface ApiResponse {
  status: number;
  success: boolean;
  message: string;
}

/**
 * 创建风格
 */
export const createStyle = async (data: StyleFormData): Promise<void> => {
  const response = await axiosInstance.post<ApiResponse>(
    `${baseUrl}/artworkAiDrawStyle/add`,
    data
  );

  if (!response.data.success) {
    throw new Error(response.data.message || '创建失败');
  }
};

/**
 * 更新风格
 */
export const updateStyle = async (
  data: StyleFormData & { styleId: number }
): Promise<void> => {
  const response = await axiosInstance.post<ApiResponse>(
    `${baseUrl}/artworkAiDrawStyle/update`,
    data
  );

  if (!response.data.success) {
    throw new Error(response.data.message || '更新失败');
  }
};

/**
 * 获取模型编号列表
 */
export const getModelOptions = async (): Promise<ModelOption[]> => {
  const response = await axiosInstance.get<{
    status: number;
    success: boolean;
    message: string;
    data: ModelOption[];
  }>(`${baseUrl}/artworkAiDrawStyle/models`);

  if (!response.data.success || !response.data.data) {
    throw new Error(response.data.message || '获取模型列表失败');
  }

  return response.data.data;
};

/**
 * 获取风格类型列表（从liblib获取）
 */
export const getStyleTypeOptions = async () => {
  const { data } = await artworkLiblibTypeStyle();
  return data.data
    .filter((item) => item.styleTypeId)
    .map((item) => ({
      styleTypeId: item.styleTypeId!,
      styleType: item.styleType || '',
      styleTypeJa: item.styleTypeJa,
    }));
};

// ========== Lora 配置 API ==========
export interface LoraConfig {
  id?: number;
  styleId?: number;
  styleSource?: string;
  loraName: string;
  loraNameEn?: string;
  loraNameJa?: string;
  loraFilePath?: string;
  loraAirId?: string;
  /** Replicate flux-dev-lora 用（SuppFluxPro model=21），HTTPS 直链 .safetensors */
  loraUrl?: string;
  loraImageUrl?: string;
  defaultWeight: number;
  bigModel?: string;
  sortOrder?: number;
}

export const getLoraList = async (styleId: number, styleSource: string = 'sx'): Promise<LoraConfig[]> => {
  const response = await axiosInstance.get<{ success: boolean; data: LoraConfig[] }>(
    `${baseUrl}/styleLoraConfig/list?styleId=${styleId}&styleSource=${styleSource}`
  );
  return response.data.success ? response.data.data || [] : [];
};

export const batchSaveLora = async (styleId: number, styleSource: string, loraList: LoraConfig[]): Promise<void> => {
  await axiosInstance.post(
    `${baseUrl}/styleLoraConfig/batchSave?styleId=${styleId}&styleSource=${styleSource}`,
    loraList
  );
};

